import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Phone, Mail, Save, User, Instagram, Globe, Shield, Ban, Plus, X, Upload, Trash2, Camera, Image, CreditCard, Crown, CheckCircle, Loader2, AlertCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { LANGUAGE_FLAGS, LANGUAGE_NAMES } from '@/i18n/translations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Facebook Icon Component  
const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export default function SettingsPage() {
  const navigate = useNavigate();
  const { getAuthHeader, user, isPro, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false); // For button feedback
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  
  const photoInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  // Payment settings
  const [settings, setSettings] = useState({
    wero_phone: '',
    paypal_email: '',
    paypal_username: ''
  });

  // Profile settings
  const [profile, setProfile] = useState({
    display_name: '',
    full_name: '',
    phone: '',
    instagram: '',
    tiktok: '',
    facebook: '',
    website: '',
    photo_url: '',
    logo_url: '',
    email_public: '',
    city: '',
    bio: '',
    review_url: '',
    preferred_language: language
  });

  // Moderation settings
  const [forbiddenWords, setForbiddenWords] = useState([]);
  const [newWord, setNewWord] = useState('');
  const [blockedClients, setBlockedClients] = useState([]);

  // Handle tab parameter from URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['profile', 'payment', 'moderation', 'subscription'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Handle subscription redirect params
  useEffect(() => {
    const subscriptionResult = searchParams.get('subscription');
    if (subscriptionResult === 'success') {
      setActiveTab('subscription');
      // Could show a toast/notification here
    } else if (subscriptionResult === 'cancelled') {
      setActiveTab('subscription');
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchAll = async () => {
      // Wait for user to be loaded
      if (!user) return;
      
      try {
        // LIFETIME PROTECTION: Check ALL possible lifetime indicators on user object
        const isLifetime = user.is_pro_lifetime === true || 
                           user.is_pro_lifetime === 'true' || 
                           user.pro_status === 'lifetime' || 
                           user.plan === 'lifetime';
        
        console.log('[SETTINGS] User lifetime check:', { 
          is_pro_lifetime: user.is_pro_lifetime, 
          pro_status: user.pro_status, 
          isLifetime 
        });
        
        // Build API calls - SKIP Stripe for lifetime users
        const apiCalls = [
          axios.get(`${API}/dj/settings`, { headers: getAuthHeader() }),
          axios.get(`${API}/dj/profile`, { headers: getAuthHeader() }),
          axios.get(`${API}/dj/forbidden-words`, { headers: getAuthHeader() }),
          axios.get(`${API}/dj/blocked-clients`, { headers: getAuthHeader() })
        ];
        
        // ONLY add Stripe call for NON-lifetime users
        if (!isLifetime) {
          apiCalls.push(
            axios.get(`${API}/stripe/subscription-status`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
          );
        }
        
        const results = await Promise.all(apiCalls);
        const [settingsRes, profileRes, wordsRes, blockedRes] = results;
        const subRes = isLifetime 
          ? { data: { has_subscription: true, pro_status: 'lifetime', trial_used: true } }
          : results[4];
        
        setSettings({
          wero_phone: settingsRes.data.wero_phone || '',
          paypal_email: settingsRes.data.paypal_email || '',
          paypal_username: settingsRes.data.paypal_username || ''
        });
        
        setProfile({
          display_name: profileRes.data.display_name || '',
          full_name: profileRes.data.full_name || '',
          phone: profileRes.data.phone || '',
          instagram: profileRes.data.instagram || '',
          tiktok: profileRes.data.tiktok || '',
          facebook: profileRes.data.facebook || '',
          website: profileRes.data.website || '',
          photo_url: profileRes.data.photo_url || '',
          logo_url: profileRes.data.logo_url || '',
          email_public: profileRes.data.email_public || '',
          city: profileRes.data.city || '',
          bio: profileRes.data.bio || '',
          review_url: profileRes.data.review_url || ''
        });
        
        setForbiddenWords(wordsRes.data.words || []);
        setBlockedClients(blockedRes.data.blocked || []);
        
        // Set subscription status
        if (subRes?.data) {
          setSubscriptionStatus(subRes.data);
        } else if (isLifetime) {
          // Fallback for lifetime
          setSubscriptionStatus({ has_subscription: true, pro_status: 'lifetime', trial_used: true });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [getAuthHeader, user]);

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await axios.put(`${API}/dj/settings`, settings, { headers: getAuthHeader() });
      setSaveSuccess(true);
      toast.success(t('settings', 'settings_saved_success'));
      // Reset success state after 2 seconds
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving settings');
      toast.error(t('settings', 'settings_saved_error'));
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await axios.put(`${API}/dj/profile`, profile, { headers: getAuthHeader() });
      setSaveSuccess(true);
      toast.success(t('settings', 'profile_saved_success'));
      // Reset success state after 2 seconds
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving profile');
      toast.error(t('settings', 'profile_saved_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (3MB max)
    if (file.size > 3 * 1024 * 1024) {
      alert('Fichier trop volumineux. Taille max: 3MB');
      return;
    }
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Type de fichier non autorisé. Types acceptés: PNG, JPG, WEBP');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    // Use new R2 endpoints
    const endpoint = fileType === 'photo' ? '/upload/avatar' : '/upload/logo';
    
    try {
      const response = await axios.post(
        `${API}${endpoint}`,
        formData,
        { 
          headers: { 
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.ok) {
        setProfile(prev => ({
          ...prev,
          [fileType === 'photo' ? 'photo_url' : 'logo_url']: response.data.url
        }));
      }
    } catch (error) {
      console.error('Error uploading file:', error.response?.data?.detail || error.message);
      alert(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
      // Reset input
      if (fileType === 'photo' && photoInputRef.current) {
        photoInputRef.current.value = '';
      }
      if (fileType === 'logo' && logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleFileDelete = async (fileType) => {
    try {
      await axios.delete(`${API}/dj/upload/${fileType}`, { headers: getAuthHeader() });
      setProfile(prev => ({
        ...prev,
        [fileType === 'photo' ? 'photo_url' : 'logo_url']: ''
      }));
    } catch (error) {
      console.error('Error deleting file');
    }
  };

  const addForbiddenWord = async () => {
    if (!newWord.trim()) return;
    const updatedWords = [...forbiddenWords, newWord.trim().toLowerCase()];
    try {
      await axios.put(`${API}/dj/forbidden-words`, { words: updatedWords }, { headers: getAuthHeader() });
      setForbiddenWords(updatedWords);
      setNewWord('');
    } catch (error) {
      console.error('Error adding word');
    }
  };

  const removeForbiddenWord = async (word) => {
    const updatedWords = forbiddenWords.filter(w => w !== word);
    try {
      await axios.put(`${API}/dj/forbidden-words`, { words: updatedWords }, { headers: getAuthHeader() });
      setForbiddenWords(updatedWords);
    } catch (error) {
      console.error('Error removing word');
    }
  };

  const unblockClient = async (clientId) => {
    try {
      await axios.delete(`${API}/dj/unblock-client/${clientId}`, { headers: getAuthHeader() });
      setBlockedClients(blockedClients.filter(c => c.client_id !== clientId));
    } catch (error) {
      console.error('Error unblocking client');
    }
  };

  const handleSubscribe = async () => {
    // LIFETIME PROTECTION - never allow checkout for lifetime users
    const isLifetime = user?.is_pro_lifetime === true || user?.is_pro_lifetime === 'true' || 
                       user?.pro_status === 'lifetime' || subscriptionStatus?.pro_status === 'lifetime';
    if (isLifetime) {
      alert('✅ Vous êtes déjà Pro Lifetime ! Pas besoin d\'abonnement.');
      return;
    }
    
    setSubscriptionLoading(true);
    try {
      const response = await axios.post(
        `${API}/stripe/create-checkout-session`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création du paiement');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setSubscriptionLoading(true);
    try {
      const response = await axios.post(
        `${API}/stripe/create-portal-session`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating portal:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'ouverture du portail');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    // Use Stripe portal for cancellation
    await handleManageSubscription();
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, {
        headers: getAuthHeader()
      });
      
      // Logout and redirect to login
      logout();
      navigate('/login');
      
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la suppression du compte');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neon-cyan animate-pulse font-heading text-2xl">{t('common', 'loading')}</div>
      </div>
    );
  }

  // Tab labels with translations
  const tabLabels = {
    profile: t('settings', 'profile'),
    payment: t('settings', 'tips_tab'),
    moderation: t('settings', 'moderation'),
    subscription: t('settings', 'subscription')
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="bg-[#0a0a0a] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('common', 'back')}
            </Button>
          </Link>
          <h1 className="font-heading text-xl text-white">{t('settings', 'title')}</h1>
          <div className="w-24" />
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Tabs - Mobile-optimized horizontal scroll */}
        <div className="relative mb-6">
          {/* Scroll container with proper padding for edge visibility */}
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide snap-x snap-mandatory">
            {[
              { id: 'profile', label: tabLabels.profile, shortLabel: 'Profil', icon: User },
              { id: 'payment', label: tabLabels.payment, shortLabel: 'Tips', icon: Mail },
              { id: 'moderation', label: tabLabels.moderation, shortLabel: 'Modér.', icon: Shield },
              { id: 'subscription', label: tabLabels.subscription, shortLabel: 'Abo', icon: Crown }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg whitespace-nowrap transition-all snap-start flex-shrink-0 min-w-fit text-sm sm:text-base ${
                  activeTab === tab.id 
                    ? 'bg-neon-cyan text-black font-semibold shadow-[0_0_15px_rgba(0,240,255,0.3)]' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 active:bg-white/15'
                }`}
              >
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                {/* Full label on sm+, short label on mobile */}
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
          {/* Fade indicator for scroll hint on mobile */}
          <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-[#0a0a0a] to-transparent pointer-events-none sm:hidden" />
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="glass rounded-2xl p-6 space-y-6">
            <div>
              <h2 className="font-heading text-xl text-white mb-2">{t('settings', 'business_card')}</h2>
              <p className="text-gray-400 text-sm">{t('settings', 'business_card_desc')}</p>
            </div>

            {/* Photo & Logo Upload Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Profile Photo */}
              <div className="space-y-3">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Camera className="w-4 h-4 text-neon-cyan" />
                  {t('settings', 'profile_photo')}
                </Label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                    {profile.photo_url ? (
                      <img src={profile.photo_url} alt="Photo" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-gray-500" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'photo')}
                      className="hidden"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploading}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? t('settings', 'uploading') : t('settings', 'upload')}
                    </Button>
                    {profile.photo_url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleFileDelete('photo')}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('settings', 'delete')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Logo */}
              <div className="space-y-3">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Image className="w-4 h-4 text-neon-pink" />
                  {t('settings', 'custom_logo')}
                </Label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                    {profile.logo_url ? (
                      <img src={profile.logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
                    ) : (
                      <Image className="w-8 h-8 text-gray-500" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'logo')}
                      className="hidden"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploading}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? t('settings', 'uploading') : t('settings', 'upload')}
                    </Button>
                    {profile.logo_url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleFileDelete('logo')}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('settings', 'delete')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="h-px bg-white/10 my-4" />

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings', 'display_name')}</Label>
                <Input
                  name="display_name"
                  value={profile.display_name}
                  onChange={handleProfileChange}
                  placeholder="DJ Awesome"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings', 'full_name')} ({t('common', 'optional')})</Label>
                <Input
                  name="full_name"
                  value={profile.full_name}
                  onChange={handleProfileChange}
                  placeholder="Jean Dupont"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-neon-cyan" />
                  {t('settings', 'phone')}
                </Label>
                <Input
                  name="phone"
                  value={profile.phone}
                  onChange={handleProfileChange}
                  placeholder="06 12 34 56 78"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="h-px bg-white/10 my-2" />
              <p className="text-gray-500 text-xs">{t('settings', 'social_links')}</p>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-neon-pink" />
                  {t('settings', 'instagram')}
                </Label>
                <Input
                  name="instagram"
                  value={profile.instagram}
                  onChange={handleProfileChange}
                  placeholder="your_instagram_profile"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <TikTokIcon className="w-4 h-4" />
                  {t('settings', 'tiktok')}
                </Label>
                <Input
                  name="tiktok"
                  value={profile.tiktok}
                  onChange={handleProfileChange}
                  placeholder="your_tiktok_profile"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <FacebookIcon className="w-4 h-4 text-blue-500" />
                  {t('settings', 'facebook')}
                </Label>
                <Input
                  name="facebook"
                  value={profile.facebook}
                  onChange={handleProfileChange}
                  placeholder="Page name or URL"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-neon-cyan" />
                  {t('settings', 'website')}
                </Label>
                <Input
                  name="website"
                  value={profile.website}
                  onChange={handleProfileChange}
                  placeholder="www.mysite.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="h-px bg-white/10 my-2" />
              <p className="text-gray-500 text-xs">{t('settings', 'additional_info')}</p>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-neon-cyan" />
                  {t('settings', 'public_email')} ({t('common', 'optional')})
                </Label>
                <Input
                  name="email_public"
                  value={profile.email_public}
                  onChange={handleProfileChange}
                  placeholder="contact@mysite.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings', 'city')} ({t('common', 'optional')})</Label>
                <Input
                  name="city"
                  value={profile.city}
                  onChange={handleProfileChange}
                  placeholder="Paris, Lyon, Marseille..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings', 'bio')} ({t('common', 'optional')})</Label>
                <textarea
                  name="bio"
                  value={profile.bio}
                  onChange={handleProfileChange}
                  placeholder="DJ for 10 years, specialized in Hip-Hop & RnB..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 focus:border-neon-cyan focus:outline-none resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings', 'review_url')} ({t('common', 'optional')})</Label>
                <Input
                  name="review_url"
                  value={profile.review_url}
                  onChange={handleProfileChange}
                  placeholder="https://g.page/r/... or https://www.facebook.com/..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
                <p className="text-gray-500 text-xs">{t('settings', 'review_url_desc')}</p>
              </div>

              <div className="h-px bg-white/10 my-4" />
              
              {/* Language Selector */}
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-neon-cyan" />
                  {t('settings', 'interface_language')}
                </Label>
                <Select
                  value={language}
                  onValueChange={(value) => {
                    setLanguage(value);
                    setProfile(prev => ({ ...prev, preferred_language: value }));
                  }}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {[
                      { code: 'fr', label: '🇫🇷 Français' },
                      { code: 'en', label: '🇬🇧 English' },
                      { code: 'es', label: '🇪🇸 Español' },
                      { code: 'de', label: '🇩🇪 Deutsch' },
                      { code: 'pt', label: '🇵🇹 Português' }
                    ].map((lang) => (
                      <SelectItem 
                        key={lang.code} 
                        value={lang.code}
                        className="text-white hover:bg-white/10"
                      >
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-gray-500 text-xs">{t('settings', 'interface_language_desc')}</p>
              </div>
            </div>

            <Button
              onClick={saveProfile}
              disabled={saving || saveSuccess}
              className={`w-full font-bold uppercase tracking-widest rounded-full py-4 transition-all duration-300 ${
                saveSuccess 
                  ? 'bg-green-500 text-white hover:bg-green-500' 
                  : 'bg-neon-cyan text-black hover:shadow-[0_0_30px_rgba(0,240,255,0.6)]'
              }`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('settings', 'saving')}
                </>
              ) : saveSuccess ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  {t('settings', 'saved')}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {t('settings', 'save_profile')}
                </>
              )}
            </Button>

            {/* Delete Account Section */}
            <div className="mt-12 pt-8 border-t border-red-500/20">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                <h3 className="text-red-400 font-heading text-lg mb-2 flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />
                  {t('settings', 'delete_account_title')}
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  {t('settings', 'delete_warning')}<br />
                  {t('settings', 'delete_account_warning')}
                </p>
                
                {subscriptionStatus?.pro_status === 'active' || subscriptionStatus?.pro_status === 'trialing' ? (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4">
                    <p className="text-orange-400 text-sm flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {t('settings', 'active_sub_warning')}
                    </p>
                  </div>
                ) : null}
                
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={subscriptionStatus?.pro_status === 'active' || subscriptionStatus?.pro_status === 'trialing' || deleteLoading}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('settings', 'delete_confirm')}
                </Button>
              </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
              <DialogContent className="bg-[#1a1a2e] border border-red-500/30 text-white">
                <DialogHeader>
                  <DialogTitle className="text-red-400 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {t('modals', 'delete_account_title')}
                  </DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-gray-300 mb-4">
                    {t('modals', 'delete_account_warning')}
                  </p>
                </div>
                <DialogFooter className="gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    {t('common', 'cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deleteLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common', 'loading')}
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('modals', 'delete_account_confirm')}
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Payment Tab */}
        {activeTab === 'payment' && (
          <div className="glass rounded-2xl p-6 space-y-6">
            <div>
              <h2 className="font-heading text-xl text-white mb-2">{t('settings', 'tips_options')}</h2>
              <p className="text-gray-400 text-sm">{t('settings', 'tips_desc')}</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings', 'paypal_name')}</Label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">paypal.me/</span>
                  <Input
                    name="paypal_username"
                    value={settings.paypal_username}
                    onChange={handleSettingsChange}
                    placeholder="your-name"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings', 'wero_number')}</Label>
                <Input
                  name="wero_phone"
                  value={settings.wero_phone}
                  onChange={handleSettingsChange}
                  placeholder="06 12 34 56 78"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                />
              </div>
            </div>

            <Button
              onClick={saveSettings}
              disabled={saving || saveSuccess}
              className={`w-full font-bold uppercase tracking-widest rounded-full py-4 transition-all duration-300 ${
                saveSuccess 
                  ? 'bg-green-500 text-white hover:bg-green-500' 
                  : 'bg-neon-cyan text-black hover:shadow-[0_0_30px_rgba(0,240,255,0.6)]'
              }`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('settings', 'saving')}
                </>
              ) : saveSuccess ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  {t('settings', 'saved')}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {t('settings', 'save_tips')}
                </>
              )}
            </Button>
          </div>
        )}

        {/* Moderation Tab */}
        {activeTab === 'moderation' && (
          <div className="space-y-6">
            {/* Forbidden Words */}
            <div className="glass rounded-2xl p-6">
              <div className="mb-4">
                <h2 className="font-heading text-xl text-white mb-2">{t('settings', 'forbidden_words')}</h2>
                <p className="text-gray-400 text-sm">{t('settings', 'forbidden_words_desc')}</p>
              </div>

              <div className="flex gap-2 mb-4">
                <Input
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  placeholder={t('settings', 'add_word')}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10"
                  onKeyPress={(e) => e.key === 'Enter' && addForbiddenWord()}
                />
                <Button onClick={addForbiddenWord} className="bg-neon-cyan text-black">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {forbiddenWords.map((word, index) => (
                  <span
                    key={index}
                    className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {word}
                    <button
                      onClick={() => removeForbiddenWord(word)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Blocked Clients */}
            <div className="glass rounded-2xl p-6">
              <div className="mb-4">
                <h2 className="font-heading text-xl text-white mb-2 flex items-center gap-2">
                  <Ban className="w-5 h-5 text-red-500" />
                  {t('settings', 'blocked_clients')}
                </h2>
                <p className="text-gray-400 text-sm">{t('settings', 'blocked_clients_desc')}</p>
              </div>

              {blockedClients.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('settings', 'no_blocked')}</p>
              ) : (
                <div className="space-y-2">
                  {blockedClients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center justify-between bg-white/5 rounded-lg p-3"
                    >
                      <span className="text-gray-300 text-sm">{client.client_id}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unblockClient(client.client_id)}
                        className="text-green-500 hover:text-green-400"
                      >
                        {t('settings', 'unblock')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Account Info */}
        <div className="mt-8 pt-8 border-t border-white/10">
          <h3 className="font-heading text-lg text-white mb-4">{t('settings', 'account')}</h3>
          <div className="space-y-2 text-sm">
            <p className="text-gray-400">
              <span className="text-gray-500">{t('settings', 'email')} :</span>{' '}
              <span className="text-white">{user?.email}</span>
            </p>
          </div>
        </div>

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="glass rounded-2xl p-6 space-y-6">
            
            {/* LIFETIME USER - SPECIAL PAGE */}
            {(user?.is_pro_lifetime === true || user?.pro_status === 'lifetime') ? (
              <div className="text-center py-8">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-500/20 to-amber-600/20 flex items-center justify-center border-2 border-yellow-500/50">
                  <span className="text-5xl">🟡</span>
                </div>
                
                <h2 className="font-heading text-3xl text-white mb-2">{t('settings', 'lifetime_title')}</h2>
                
                <Badge className="bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold text-lg px-4 py-2 mb-6">
                  ✅ {t('settings', 'lifetime_badge')}
                </Badge>
                
                <div className="max-w-md mx-auto">
                  <p className="text-xl text-white mb-4">
                    {t('settings', 'lifetime_desc')}
                  </p>
                  
                  <p className="text-gray-400 mb-8">
                    {t('settings', 'lifetime_thanks')}
                  </p>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                    <h3 className="text-yellow-400 font-semibold mb-4 flex items-center justify-center gap-2">
                      <Crown className="w-5 h-5" />
                      {t('settings', 'lifetime_advantages')}
                    </h3>
                    <ul className="space-y-3 text-left">
                      {[
                        t('settings', 'unlimited_events'),
                        t('settings', 'custom_qr'),
                        t('settings', 'stats_reports'),
                        t('settings', 'integrated_tips'),
                        t('settings', 'popularity_sort'),
                        t('settings', 'hot_badge'),
                        t('settings', 'priority_support'),
                        t('settings', 'no_subscription')
                      ].map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-gray-300">
                          <CheckCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              /* NON-LIFETIME USERS - REGULAR SUBSCRIPTION PAGE */
              <>
                <div>
                  <h2 className="font-heading text-xl text-white mb-2 flex items-center gap-2">
                    <Crown className="w-6 h-6 text-neon-pink" />
                    {t('settings', 'subscription_title')}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    {t('settings', 'subscription_desc')}
                  </p>
                </div>

                {/* Current Plan Status */}
                <div className="bg-[#1a1a2e] rounded-xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-gray-400 text-sm">{t('settings', 'current_plan')}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {(subscriptionStatus?.pro_status === 'active' || subscriptionStatus?.pro_status === 'trialing') ? (
                          <>
                            <Badge className="bg-gradient-to-r from-neon-pink to-neon-cyan text-white">
                              {subscriptionStatus?.pro_status === 'active' ? `🟢 ${t('settings', 'founder')}` : 'PRO'}
                            </Badge>
                            <Badge variant="outline" className={
                              subscriptionStatus?.pro_status === 'trialing' 
                                ? "border-yellow-500 text-yellow-400"
                                : "border-green-500 text-green-400"
                            }>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {subscriptionStatus?.pro_status === 'trialing' ? t('settings', 'trial') : `4,99€ ${t('settings', 'lifetime_rate')}`}
                            </Badge>
                            {subscriptionStatus?.cancel_at_period_end === true && (
                              <Badge variant="outline" className="border-orange-500 text-orange-400">
                                {t('settings', 'cancel_scheduled')}
                              </Badge>
                            )}
                          </>
                        ) : subscriptionStatus?.pro_status === 'past_due' ? (
                          <>
                            <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
                              {t('settings', 'payment_overdue')}
                            </Badge>
                          </>
                        ) : (
                          <Badge variant="outline" className="border-gray-500 text-gray-400">
                            FREE
                          </Badge>
                        )}
                      </div>
                    </div>
                {subscriptionStatus?.pro_status !== 'lifetime' && (subscriptionStatus?.current_period_end || subscriptionStatus?.trial_end) && (
                  <div className="text-right">
                    <p className="text-gray-400 text-xs">
                      {subscriptionStatus?.pro_status === 'trialing' 
                        ? t('settings', 'trial_ends')
                        : subscriptionStatus?.cancel_at_period_end === true 
                          ? t('settings', 'subscription_ends')
                          : t('settings', 'next_billing')}
                    </p>
                    <p className="text-white text-sm">
                      {(() => {
                        const timestamp = subscriptionStatus?.pro_status === 'trialing' && subscriptionStatus?.trial_end 
                          ? subscriptionStatus.trial_end 
                          : subscriptionStatus.current_period_end;
                        
                        if (!timestamp) return null;
                        
                        // Stripe sends Unix timestamp in seconds, JS Date needs milliseconds
                        const date = new Date(timestamp * 1000);
                        
                        // Validate date is reasonable (after year 2020)
                        if (date.getFullYear() < 2020) return null;
                        
                        const localeMap = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES', de: 'de-DE', pt: 'pt-PT' };
                        return date.toLocaleDateString(localeMap[language] || 'fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        });
                      })()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* PRO Upgrade Banner - Only for FREE users (NOT lifetime) */}
            {(!subscriptionStatus?.pro_status || subscriptionStatus?.pro_status === 'free' || subscriptionStatus?.pro_status === 'canceled') && 
             subscriptionStatus?.pro_status !== 'lifetime' && !user?.is_pro_lifetime && (
              <div className="bg-gradient-to-r from-neon-pink/20 via-purple-500/20 to-neon-cyan/20 border-2 border-neon-pink/50 rounded-xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-neon-pink/10 rounded-full blur-3xl"></div>
                <div className="relative">
                  {/* Offre Fondateur */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 mb-4 inline-block">
                    <p className="text-yellow-400 text-sm font-medium">
                      🎉 {t('settings', 'founder_offer')}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🚀</span>
                    <h3 className="font-heading text-xl text-white">{t('settings', 'upgrade_cta')}</h3>
                  </div>
                  <p className="text-gray-300 mb-4">{t('settings', 'unlock_features')}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                    {[
                      t('settings', 'event_qr'),
                      t('settings', 'integrated_tips'),
                      t('settings', 'advanced_stats'),
                      t('settings', 'pdf_export'),
                      t('settings', 'dj_card')
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-200">
                        <CheckCircle className="w-4 h-4 text-neon-cyan flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="text-center sm:text-left">
                      <p className="text-white font-heading text-2xl">4,99€<span className="text-gray-400 text-base font-normal">/mois</span></p>
                      <p className="text-neon-cyan text-sm">{t('settings', 'free_trial')}</p>
                      <p className="text-gray-500 text-xs">{t('settings', 'cancel_anytime')}</p>
                    </div>
                    <Button
                      onClick={handleSubscribe}
                      disabled={subscriptionLoading}
                      className="w-full sm:w-auto bg-gradient-to-r from-neon-pink to-purple-500 hover:shadow-[0_0_30px_rgba(255,0,153,0.5)] text-white font-bold py-3 px-8 rounded-full transition-all"
                      data-testid="subscribe-pro-banner-btn"
                    >
                      {subscriptionLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common', 'loading')}
                        </>
                      ) : (
                        <>
                          <Crown className="w-4 h-4 mr-2" />
                          {t('settings', 'start_trial')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* PRO Features */}
            <div className="bg-gradient-to-br from-neon-pink/10 to-neon-cyan/10 border border-neon-pink/30 rounded-xl p-5">
              <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
                {t('settings', 'pro_advantages')}
              </h3>
              <ul className="space-y-3">
                {[
                  t('settings', 'unlimited_events'),
                  t('settings', 'custom_qr'),
                  t('settings', 'stats_reports'),
                  t('settings', 'popularity_sort'),
                  t('settings', 'hot_badge'),
                  t('settings', 'priority_support')
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-neon-pink flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing & Action - Hidden for lifetime users */}
            {subscriptionStatus?.pro_status !== 'lifetime' && !user?.is_pro_lifetime && (
              <div className="bg-[#1a1a2e] rounded-xl p-5 border border-neon-pink/30">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-white font-heading text-2xl">4,99€<span className="text-gray-400 text-base font-normal">/mois</span></p>
                    {!subscriptionStatus?.trial_used ? (
                      <p className="text-neon-cyan text-sm mt-1">{t('settings', 'free_trial')}</p>
                    ) : (
                      <p className="text-gray-400 text-sm mt-1">{t('settings', 'cancel_anytime')}</p>
                    )}
                  </div>
                </div>

                {subscriptionStatus?.pro_status !== 'active' && subscriptionStatus?.pro_status !== 'trialing' ? (
                  <Button
                    onClick={handleSubscribe}
                    disabled={subscriptionLoading}
                    className="w-full bg-gradient-to-r from-neon-pink to-neon-cyan hover:opacity-90 text-white font-semibold py-3"
                    data-testid="subscribe-pro-btn"
                  >
                    {subscriptionLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common', 'loading')}
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        {!subscriptionStatus?.trial_used ? t('settings', 'start_trial') : t('settings', 'subscribe')}
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      onClick={handleManageSubscription}
                      disabled={subscriptionLoading}
                      className="w-full border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10"
                      data-testid="manage-subscription-btn"
                    >
                      {subscriptionLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common', 'loading')}
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          {t('settings', 'manage_subscription')}
                        </>
                      )}
                  </Button>
                  <p className="text-gray-500 text-xs text-center">
                    {t('settings', 'manage_hint')}
                  </p>
                </div>
              )}
            </div>
            )}

            {subscriptionStatus?.pro_status !== 'lifetime' && (
              <p className="text-gray-500 text-xs text-center">
                {t('settings', 'secure_payment')}
              </p>
            )}
          </>
            )}
          </div>
        )}

        {/* Support & Contact Section - Always visible */}
        <div className="glass rounded-2xl p-6 mt-6">
          <h3 className="font-heading text-lg text-white mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5 text-neon-cyan" />
            {t('settings', 'support_title')}
          </h3>
          <p className="text-gray-300 mb-2">{t('settings', 'support_question')}</p>
          <p className="text-gray-400 text-sm mb-4">
            {t('settings', 'support_desc')}
          </p>
          <a href="mailto:contact@trackvibes.app">
            <Button 
              className="bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30 hover:bg-neon-cyan/30"
              data-testid="contact-support-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {t('settings', 'contact_support')}
            </Button>
          </a>
        </div>

        {/* Version & Footer */}
        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <p className="text-gray-400 text-sm">TrackVibes v1.0.0</p>
          <p className="text-gray-500 text-xs mt-1">© 2026 M. Lasledj</p>
          <button
            onClick={() => setShowChangelog(true)}
            className="text-neon-cyan text-xs hover:underline mt-2 inline-block"
            data-testid="view-changelog-btn"
          >
            {t('settings', 'whats_new')}
          </button>
          
          {/* Legal Links */}
          <nav className="flex justify-center gap-4 text-xs text-gray-500 mt-4">
            <Link to="/privacy" className="hover:text-gray-300">{t('settings', 'privacy')}</Link>
            <span className="text-gray-700">|</span>
            <Link to="/conditions" className="hover:text-gray-300">{t('settings', 'terms')}</Link>
            <span className="text-gray-700">|</span>
            <Link to="/mentions-legales" className="hover:text-gray-300">{t('settings', 'legal')}</Link>
            <span className="text-gray-700">|</span>
            <a href="mailto:contact@trackvibes.app" className="hover:text-gray-300">{t('settings', 'contact')}</a>
          </nav>
        </div>

        {/* Changelog Modal */}
        <Dialog open={showChangelog} onOpenChange={setShowChangelog}>
          <DialogContent className="bg-[#1a1a2e] border border-neon-cyan/30 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2 text-xl">
                <span className="text-2xl">🚀</span>
                Version 1.0.0
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <ul className="space-y-3">
                {[
                  t('settings', 'event_qr'),
                  t('settings', 'free_trial'),
                  t('settings', 'advanced_stats'),
                  t('settings', 'pdf_export'),
                  t('settings', 'dj_card'),
                  t('settings', 'integrated_tips'),
                  t('settings', 'priority_support')
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-neon-cyan flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <DialogFooter>
              <Button
                onClick={() => setShowChangelog(false)}
                className="w-full bg-neon-cyan text-black font-bold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
              >
                {t('common', 'close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
